// src/pages/PaymentSuccess.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  Clipboard,
  Home,
  LoaderCircle,
  LockKeyhole,
  LogIn,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";

import "../sass/pages/paymentSuccess.scss";
import Logo from "../assets/logoClaro.webp";
import { useToast } from "../context/ToastContext";
import { trackCompleteRegistrationOnce } from "../services/metaPixel";

const API_BASE = String(
  process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const CHECKOUT_STORAGE_KEYS = [
  "checkoutPlanName",
  "checkoutPlanPrice",
  "checkoutTipoUsuario",
  "checkoutCrp",
  "checkoutEmail",
  "checkoutSessionIdTemp",
  "checkoutPriceId",
  "checkoutCouponCode",
  "checkoutCouponName",
  "checkoutCouponDiscount",
  "checkoutCouponMonths",
  "checkoutCouponApplied",
];

const TEMP_STORAGE_KEYS = [
  "psy_temp_register_session_id",
  "psy_temp_register_email",
  "psy_temp_register_plan_name",
  "psy_temp_register_plan_price",
  "psy_temp_register_tipo_usuario",
  "psy_temp_register_price_id",
];

const FLOW_STORAGE_KEYS = [
  "psyassist:selected-plan",
  "selectedPlan",
  "psyassist:registration-flow",
];

const FINALIZATION_REQUESTS = new Map();

const safeParseJson = (value, fallback = null) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const removeStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // A limpeza dos demais dados ainda poderá continuar.
  }
};

const firstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      const value = source[key];

      if (value !== undefined && value !== null && String(value).trim()) {
        return value;
      }
    }
  }

  return fallback;
};

const resolveFlowData = (locationState = {}) => {
  const registrationFlow =
    safeParseJson(
      readStorage(sessionStorage, "psyassist:registration-flow"),
      null
    ) || {};

  const selectedPlan =
    safeParseJson(
      readStorage(sessionStorage, "psyassist:selected-plan"),
      null
    ) || {};

  const legacySelectedPlan =
    safeParseJson(readStorage(sessionStorage, "selectedPlan"), null) || {};

  const checkoutFlow = {
    planName: readStorage(localStorage, "checkoutPlanName"),
    planPrice: readStorage(localStorage, "checkoutPlanPrice"),
    tipoUsuario: readStorage(localStorage, "checkoutTipoUsuario"),
    email: readStorage(localStorage, "checkoutEmail"),
    cupomCodigo: readStorage(localStorage, "checkoutCouponCode"),
    cupomNome: readStorage(localStorage, "checkoutCouponName"),
    cupomDescontoPercentual: readStorage(
      localStorage,
      "checkoutCouponDiscount"
    ),
    cupomDuracaoMeses: readStorage(
      localStorage,
      "checkoutCouponMonths"
    ),
    cupomAplicado: readStorage(
      localStorage,
      "checkoutCouponApplied"
    ),
  };

  const temporaryFlow = {
    planName: readStorage(localStorage, "psy_temp_register_plan_name"),
    planPrice: readStorage(localStorage, "psy_temp_register_plan_price"),
    tipoUsuario: readStorage(localStorage, "psy_temp_register_tipo_usuario"),
    email: readStorage(localStorage, "psy_temp_register_email"),
  };

  const sources = [
    locationState,
    registrationFlow,
    selectedPlan,
    legacySelectedPlan,
    checkoutFlow,
    temporaryFlow,
  ];

  const tipoUsuario =
    String(firstValue(sources, ["tipoUsuario"], "individual")).toLowerCase() ===
    "clinica"
      ? "clinica"
      : "individual";

  const individualPrice = firstValue(
    sources,
    ["planPrice", "planPriceIndividual", "valorPlano", "pricePayload"],
    ""
  );

  const clinicPrice = firstValue(
    sources,
    [
      "planPrice",
      "planPriceClinica",
      "planPriceIndividual",
      "valorPlano",
      "pricePayload",
    ],
    individualPrice
  );

  return {
    planName: String(
      firstValue(sources, ["planName", "plano", "name"], "Plano contratado")
    ).trim(),
    planPrice: String(
      tipoUsuario === "clinica" ? clinicPrice : individualPrice
    ).trim(),
    tipoUsuario,
    email: String(firstValue(sources, ["email"], ""))
      .trim()
      .toLowerCase(),

    cupomAplicado:
      String(
        firstValue(sources, ["cupomAplicado"], false)
      ).toLowerCase() === "true",

    cupomCodigo: String(
      firstValue(sources, ["cupomCodigo"], "")
    )
      .trim()
      .toUpperCase(),

    cupomNome: String(
      firstValue(sources, ["cupomNome"], "")
    ).trim(),

    cupomDescontoPercentual: Math.max(
      Number(
        firstValue(
          sources,
          ["cupomDescontoPercentual"],
          0
        )
      ) || 0,
      0
    ),

    cupomDuracaoMeses: Math.max(
      Number(
        firstValue(
          sources,
          ["cupomDuracaoMeses"],
          0
        )
      ) || 0,
      0
    ),

    trialDays: Math.max(
      Number(
        firstValue(
          sources,
          ["trialDays"],
          0
        )
      ) || 0,
      0
    ),
  };
};

const parseApiResponse = async (response) => {
  const raw = await response.text();

  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch {
    return {
      message:
        "O servidor retornou uma resposta que não pôde ser interpretada.",
      code: "INVALID_SERVER_RESPONSE",
    };
  }
};

const finalizeRegistrationRequest = (sessionId) => {
  if (FINALIZATION_REQUESTS.has(sessionId)) {
    return FINALIZATION_REQUESTS.get(sessionId);
  }

  const request = fetch(
    `${API_BASE}/psy/finalizar-cadastro/${encodeURIComponent(sessionId)}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
      },
    }
  )
    .then(async (response) => {
      const data = await parseApiResponse(response);

      return {
        ok: response.ok && data?.success !== false,
        httpStatus: response.status,
        data,
      };
    })
    .finally(() => {
      FINALIZATION_REQUESTS.delete(sessionId);
    });

  FINALIZATION_REQUESTS.set(sessionId, request);

  return request;
};

const parseMoneyValue = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return null;

  const match = raw.match(
    /(\d{1,3}(?:\.\d{3})*,\d{2}|\d+(?:[.,]\d{1,2})?)/
  );

  if (!match) return null;

  let normalized = match[1];

  if (normalized.includes(",")) {
    normalized = normalized
      .replace(/\./g, "")
      .replace(",", ".");
  } else {
    normalized = normalized.replace(",", ".");
  }

  const number = Number(normalized);

  return Number.isFinite(number)
    ? number
    : null;
};

const formatMoneyBR = (value) => {
  if (!Number.isFinite(value)) return "";

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const maskReference = (value = "") => {
  const normalized = String(value || "").trim();

  if (!normalized) return "Não disponível";
  if (normalized.length <= 8) return normalized;

  return `••••••${normalized.slice(-8)}`;
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { sessionId: sessionIdParam } = useParams();

  const [flowData, setFlowData] = useState(() =>
    resolveFlowData(location.state || {})
  );

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState(
    "Estamos validando sua assinatura e preparando o primeiro acesso."
  );
  const [httpStatus, setHttpStatus] = useState(null);
  const [errorCode, setErrorCode] = useState("");
  const [retryVersion, setRetryVersion] = useState(0);
  const [membroFundador, setMembroFundador] = useState(null);

  useEffect(() => {
    setFlowData(resolveFlowData(location.state || {}));
  }, [location.state]);

  const sessionId = useMemo(() => {
    if (sessionIdParam) {
      try {
        return decodeURIComponent(sessionIdParam).trim();
      } catch {
        return String(sessionIdParam || "").trim();
      }
    }

    const searchParams = new URLSearchParams(location.search);

    return String(
      searchParams.get("session_id") ||
        searchParams.get("sessionId") ||
        ""
    ).trim();
  }, [location.search, sessionIdParam]);

  const clearRegistrationFlow = useCallback(() => {
    [...CHECKOUT_STORAGE_KEYS, ...TEMP_STORAGE_KEYS].forEach((key) => {
      removeStorage(localStorage, key);
      removeStorage(sessionStorage, key);
    });

    FLOW_STORAGE_KEYS.forEach((key) => {
      removeStorage(localStorage, key);
      removeStorage(sessionStorage, key);
    });
  }, []);

  useEffect(() => {
    let active = true;

    const finalizeRegistration = async () => {
      setStatus("loading");
      setHttpStatus(null);
      setErrorCode("");
      setMembroFundador(null);
      setMessage(
        "Estamos validando sua assinatura e preparando o primeiro acesso."
      );

      if (!sessionId) {
        if (!active) return;

        setStatus("error");
        setMessage(
          "Não foi possível identificar a sessão retornada pelo checkout."
        );
        setErrorCode("SESSION_ID_MISSING");
        return;
      }

      try {
        const result = await finalizeRegistrationRequest(sessionId);

        if (!active) return;

        setHttpStatus(result.httpStatus);

        if (!result.ok) {
          setStatus("error");
          setErrorCode(
            String(result.data?.code || "FINALIZATION_FAILED")
          );
          setMessage(
            result.data?.message ||
              "A assinatura não pôde ser validada neste momento."
          );
          return;
        }

        const founderFromBackend =
          result.data?.membroFundador?.ativo === true;

        const founderFromFlow =
          flowData.cupomAplicado === true &&
          flowData.cupomCodigo === "FUNDADOR";

        if (founderFromBackend || founderFromFlow) {
          setMembroFundador({
            ativo: true,
            concedidoEm:
              result.data?.membroFundador?.concedidoEm ||
              new Date().toISOString(),
            origem:
              result.data?.membroFundador?.origem ||
              "CUPOM_FUNDADOR",
          });
        }

        setStatus("success");
        setMessage(
          result.data?.message ||
            "Assinatura confirmada. Sua conta está pronta para o primeiro acesso."
        );

        // Conversão Meta:
        // dispara somente depois que o backend confirmou a finalização
        // e somente uma vez por sessão de checkout neste navegador.
        trackCompleteRegistrationOnce(sessionId);

        clearRegistrationFlow();
      } catch (error) {
        console.error("Erro ao finalizar cadastro:", error);

        if (!active) return;

        setStatus("error");
        setErrorCode("NETWORK_ERROR");
        setMessage(
          "Não foi possível comunicar com o servidor para concluir a ativação."
        );
      }
    };

    finalizeRegistration();

    return () => {
      active = false;
    };
  }, [
    clearRegistrationFlow,
    flowData.cupomAplicado,
    flowData.cupomCodigo,
    retryVersion,
    sessionId,
  ]);

  const handleRetry = () => {
    setRetryVersion((current) => current + 1);
  };

  const handleGoLogin = () => {
    navigate("/login", {
      replace: true,
      state: {
        notice: {
          type: "success",
          title: "Conta ativada",
          message: "Entre com o e-mail e a senha definidos no cadastro.",
        },
      },
    });
  };

  const handleGoHome = () => {
    navigate("/", {
      replace: true,
    });
  };

  const accountTypeLabel =
    flowData.tipoUsuario === "clinica"
      ? "Conta clínica"
      : "Conta profissional";

  const normalPlanValue = useMemo(
    () => parseMoneyValue(flowData.planPrice),
    [flowData.planPrice]
  );

  const promotionalPlanValue = useMemo(() => {
    if (
      !flowData.cupomAplicado ||
      !flowData.cupomDescontoPercentual ||
      !Number.isFinite(normalPlanValue)
    ) {
      return null;
    }

    return (
      normalPlanValue *
      (1 - flowData.cupomDescontoPercentual / 100)
    );
  }, [
    flowData.cupomAplicado,
    flowData.cupomDescontoPercentual,
    normalPlanValue,
  ]);

  const promotionalPlanPrice = useMemo(
    () =>
      Number.isFinite(promotionalPlanValue)
        ? `${formatMoneyBR(promotionalPlanValue)}/mês`
        : "",
    [promotionalPlanValue]
  );

  const discountAmountValue = useMemo(() => {
    if (
      !Number.isFinite(normalPlanValue) ||
      !Number.isFinite(promotionalPlanValue)
    ) {
      return null;
    }

    return Math.max(
      normalPlanValue - promotionalPlanValue,
      0
    );
  }, [
    normalPlanValue,
    promotionalPlanValue,
  ]);

  const discountAmountPrice = useMemo(
    () =>
      Number.isFinite(discountAmountValue)
        ? formatMoneyBR(discountAmountValue)
        : "",
    [discountAmountValue]
  );

  const couponPeriodLabel = useMemo(() => {
    if (flowData.cupomDuracaoMeses > 0) {
      return `${flowData.cupomDuracaoMeses} ${
        flowData.cupomDuracaoMeses === 1 ? "mês" : "meses"
      }`;
    }

    return "período promocional";
  }, [flowData.cupomDuracaoMeses]);

  const trialLabel = useMemo(() => {
    if (flowData.trialDays <= 0) return "";

    return `${flowData.trialDays} ${
      flowData.trialDays === 1 ? "dia" : "dias"
    } grátis`;
  }, [flowData.trialDays]);

  const supportCode =
    errorCode || (httpStatus ? `HTTP_${httpStatus}` : "");

  const supportReference = maskReference(sessionId);

  const copySupportReference = async () => {
    const content = [
      supportCode ? `Código: ${supportCode}` : "",
      sessionId ? `Sessão: ${sessionId}` : "",
      httpStatus ? `HTTP: ${httpStatus}` : "",
    ]
      .filter(Boolean)
      .join("\n");

    if (!content) return;

    try {
      await navigator.clipboard.writeText(content);

      toast.success({
        title: "Referência copiada",
        message:
          "Os dados técnicos foram copiados para a área de transferência.",
      });
    } catch {
      toast.warning({
        title: "Não foi possível copiar",
        message:
          "Selecione os dados manualmente ao falar com o suporte.",
      });
    }
  };

  const visualStatus = {
    loading: {
      badge: "Validando assinatura",
      title: "Estamos ativando sua conta",
      icon: <LoaderCircle size={34} aria-hidden="true" />,
    },
    success: {
      badge: "Assinatura confirmada",
      title: "Sua conta está pronta",
      icon: <CheckCircle2 size={34} aria-hidden="true" />,
    },
    error: {
      badge: "Ativação pendente",
      title: "Não foi possível concluir",
      icon: <XCircle size={34} aria-hidden="true" />,
    },
  }[status];

  const processSteps = [
    {
      key: "return",
      title: "Retorno do checkout",
      description: "Sessão de pagamento recebida.",
      state: sessionId
        ? "done"
        : status === "error"
          ? "error"
          : "current",
    },
    {
      key: "validation",
      title: "Validação da assinatura",
      description: "Confirmação realizada no servidor.",
      state:
        status === "success"
          ? "done"
          : status === "error"
            ? "error"
            : "current",
    },
    {
      key: "activation",
      title: "Liberação do acesso",
      description: "Conta disponibilizada para login.",
      state: status === "success" ? "done" : "pending",
    },
  ];

  return (
    <main className="psy-paysuc-page">
      <div className="psy-paysuc-background" aria-hidden="true">
        <span className="psy-paysuc-background__orb psy-paysuc-background__orb--one" />
        <span className="psy-paysuc-background__orb psy-paysuc-background__orb--two" />
        <span className="psy-paysuc-background__grid" />
      </div>

      <div className="psy-paysuc-layout">
        <aside className="psy-paysuc-aside">
          <div className="psy-paysuc-brand">
            <img src={Logo} alt="PsyAssist Pro" />

            <div>
              <strong>PsyAssist Pro</strong>
              <span>Tecnologia que trabalha por você</span>
            </div>
          </div>

          <div className="psy-paysuc-aside__content">
            <span className="psy-paysuc-kicker">
              <Sparkles size={15} />
              Conclusão do onboarding
            </span>

            <h1>Um novo espaço para organizar sua prática profissional.</h1>

            <p>
              O PsyAssist Pro centraliza rotina clínica, agenda, financeiro e
              inteligência operacional em uma experiência única.
            </p>

            <div className="psy-paysuc-assurances">
              <div className="psy-paysuc-assurance">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>Validação no servidor</strong>
                  <small>
                    O navegador não decide se a assinatura foi aprovada.
                  </small>
                </div>
              </div>

              <div className="psy-paysuc-assurance">
                <span>
                  <BadgeCheck size={19} />
                </span>

                <div>
                  <strong>Ativação vinculada</strong>
                  <small>
                    O acesso é associado ao cadastro confirmado.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-paysuc-plan-card">
            <div className="psy-paysuc-plan-card__top">
              <span>{accountTypeLabel}</span>
              <CheckCircle2 size={18} />
            </div>

            <strong>{flowData.planName}</strong>

            {flowData.cupomAplicado && promotionalPlanPrice ? (
              <div className="psy-paysuc-plan-card__promo-price">
                <span>{flowData.planPrice}</span>
                <p>{promotionalPlanPrice}</p>
              </div>
            ) : flowData.planPrice ? (
              <p>{flowData.planPrice}</p>
            ) : null}

            <small>
              {flowData.cupomAplicado && promotionalPlanPrice
                ? `${flowData.cupomDescontoPercentual}% de desconto aplicado por ${couponPeriodLabel}.`
                : "Os dados comerciais exibidos são apenas um resumo visual. A validação final é realizada pelo backend."}
            </small>
          </div>
        </aside>

        <section className="psy-paysuc-panel">
          <div className="psy-paysuc-progress">
            <div className="psy-paysuc-progress__header">
              <span>Etapa concluída</span>
              <strong>Ativação da conta</strong>
            </div>

            <div
              className="psy-paysuc-progress__track"
              role="progressbar"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={
                status === "success" ? 100 : status === "error" ? 66 : 82
              }
              aria-label="Progresso da ativação"
            >
              <span
                style={{
                  width:
                    status === "success"
                      ? "100%"
                      : status === "error"
                        ? "66%"
                        : "82%",
                }}
              />
            </div>
          </div>

          <section
            className={`psy-paysuc-status psy-paysuc-status--${status}`}
            aria-live="polite"
          >
            <div className="psy-paysuc-status__visual">
              <div className="psy-paysuc-status__rings">
                <span />
                <span />
              </div>

              <div className="psy-paysuc-status__icon">
                {visualStatus.icon}
              </div>
            </div>

            <span className="psy-paysuc-status__badge">
              {visualStatus.badge}
            </span>

            <h2>{visualStatus.title}</h2>
            <p>{message}</p>
          </section>


          {status === "success" &&
          flowData.cupomAplicado &&
          flowData.cupomCodigo &&
          promotionalPlanPrice ? (
            <section
              className="psy-paysuc-promotion"
              aria-label="Benefício promocional aplicado"
            >
              <div className="psy-paysuc-promotion__mark">
                <BadgeCheck size={20} />
              </div>

              <div className="psy-paysuc-promotion__content">
                <span>Cupom confirmado</span>
                <strong>{flowData.cupomCodigo}</strong>

                <p>
                  {flowData.cupomDescontoPercentual}% de desconto por{" "}
                  {couponPeriodLabel}. Sua mensalidade promocional será de{" "}
                  <b>{promotionalPlanPrice}</b>.
                </p>

                {flowData.trialDays > 0 ? (
                  <small>
                    {trialLabel}. A primeira cobrança ocorrerá após o período
                    de teste, seguindo a condição promocional.
                  </small>
                ) : null}
              </div>

              <div className="psy-paysuc-promotion__value">
                <small>economia mensal</small>
                <strong>
                  {discountAmountPrice || "—"}
                </strong>
              </div>
            </section>
          ) : null}

          {status === "success" && membroFundador?.ativo ? (
            <section
              className="psy-paysuc-founder"
              aria-label="Membro Fundador PsyAssist Pro"
            >
              <div className="psy-paysuc-founder__shine" aria-hidden="true" />

              <div className="psy-paysuc-founder__mark">
                <Sparkles size={21} />
              </div>

              <div className="psy-paysuc-founder__content">
                <span>Founder Edition • 2026</span>
                <strong>Você é um Membro Fundador</strong>
                <p>
                  Você faz parte da primeira geração do PsyAssist Pro.
                  Seu selo de fundador é permanente.
                </p>
              </div>

              <div className="psy-paysuc-founder__edition">
                001
              </div>
            </section>
          ) : null}

          <div className="psy-paysuc-steps">
            {processSteps.map((step) => (
              <div
                key={step.key}
                className={`psy-paysuc-step psy-paysuc-step--${step.state}`}
              >
                <div className="psy-paysuc-step__icon">
                  {step.state === "done" ? (
                    <Check size={16} />
                  ) : step.state === "error" ? (
                    <AlertTriangle size={16} />
                  ) : step.state === "current" ? (
                    <LoaderCircle size={16} />
                  ) : (
                    <LockKeyhole size={15} />
                  )}
                </div>

                <div>
                  <strong>{step.title}</strong>
                  <span>{step.description}</span>
                </div>
              </div>
            ))}
          </div>

          <section className="psy-paysuc-summary">
            <div className="psy-paysuc-summary__head">
              <div>
                <span>Resumo da contratação</span>
                <strong>{flowData.planName}</strong>
              </div>

              <BadgeCheck size={20} />
            </div>

            <dl className="psy-paysuc-summary__list">
              <div>
                <dt>Tipo de conta</dt>
                <dd>{accountTypeLabel}</dd>
              </div>

              {flowData.planPrice ? (
                <div>
                  <dt>
                    {flowData.cupomAplicado
                      ? "Preço normal"
                      : "Valor apresentado"}
                  </dt>
                  <dd>{flowData.planPrice}</dd>
                </div>
              ) : null}

              {flowData.cupomAplicado && flowData.cupomCodigo ? (
                <>
                  <div>
                    <dt>Cupom</dt>
                    <dd>
                      {flowData.cupomCodigo}
                      {flowData.cupomDescontoPercentual
                        ? ` • ${flowData.cupomDescontoPercentual}% OFF`
                        : ""}
                      {flowData.cupomDuracaoMeses
                        ? ` • ${flowData.cupomDuracaoMeses} meses`
                        : ""}
                    </dd>
                  </div>

                  {promotionalPlanPrice ? (
                    <div className="psy-paysuc-summary__promotion-row">
                      <dt>Valor promocional</dt>
                      <dd>{promotionalPlanPrice}</dd>
                    </div>
                  ) : null}

                  {discountAmountPrice ? (
                    <div>
                      <dt>Economia por mês</dt>
                      <dd>{discountAmountPrice}</dd>
                    </div>
                  ) : null}

                  {flowData.trialDays > 0 ? (
                    <div>
                      <dt>Período de teste</dt>
                      <dd>{trialLabel}</dd>
                    </div>
                  ) : null}

                  {flowData.cupomDuracaoMeses > 0 ? (
                    <div>
                      <dt>Após a promoção</dt>
                      <dd>{flowData.planPrice}</dd>
                    </div>
                  ) : null}
                </>
              ) : null}

              <div>
                <dt>Referência</dt>
                <dd className="psy-paysuc-summary__mono">
                  {supportReference}
                </dd>
              </div>

              {status === "error" && supportCode ? (
                <div>
                  <dt>Código de suporte</dt>
                  <dd className="psy-paysuc-summary__mono">
                    {supportCode}
                  </dd>
                </div>
              ) : null}
            </dl>

            {status === "error" ? (
              <button
                type="button"
                className="psy-paysuc-copy"
                onClick={copySupportReference}
              >
                <Clipboard size={15} />
                Copiar referência técnica
              </button>
            ) : null}
          </section>

          <div className="psy-paysuc-actions">
            {status === "success" ? (
              <button
                type="button"
                className="psy-paysuc-primary"
                onClick={handleGoLogin}
              >
                <LogIn size={18} />
                Entrar no PsyAssist
                <ArrowRight size={17} />
              </button>
            ) : null}

            {status === "error" ? (
              <button
                type="button"
                className="psy-paysuc-primary"
                onClick={handleRetry}
              >
                <RefreshCw size={17} />
                Tentar novamente
              </button>
            ) : null}

            <button
              type="button"
              className="psy-paysuc-secondary"
              onClick={handleGoHome}
              disabled={status === "loading"}
            >
              <Home size={17} />
              Voltar ao site
            </button>
          </div>

          <footer className="psy-paysuc-footer">
            {status === "loading" ? (
              <>
                <LoaderCircle size={14} />
                <span>
                  Não feche esta página enquanto concluímos a ativação.
                </span>
              </>
            ) : status === "error" ? (
              <>
                <AlertTriangle size={14} />
                <span>
                  Caso o erro continue, encaminhe a referência ao suporte.
                </span>
              </>
            ) : (
              <>
                <ShieldCheck size={14} />
                <span>Acesso liberado para o e-mail cadastrado.</span>
              </>
            )}
          </footer>
        </section>
      </div>
    </main>
  );
};

export default PaymentSuccess;
